import React, { useEffect, useMemo, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { ExitConfirmDialog, ReportProvider } from '@/components/common';
import { PAGE_CONTAINER_ID } from '@/components/common/WetvReport/constants';
import { useRouteReport } from '@/components/common/WetvReport/useRouteReport';
import { useBackNavigation } from '@/hooks/useBackNavigation';
import { useChannelStore } from '@/stores/channelStore';
import { initSpatialNavigation } from '@/utils/navigation';
import { forceCloseKeyboard } from '@/utils/keyboardController';
import { initResponsive } from '@/utils/responsive';
import { usePlayerStore } from '@/stores/playerStore';
import { logger } from '@/utils/devUtils';
import { notifySplashReady } from '@/utils/splashUtils';

import { Header } from './Header/index';
import styles from './Layout.module.scss';

export interface LayoutProps {
  children: React.ReactNode;
  showHeader?: boolean; // 是否显示 Header，默认 true
}

// 不显示 Header 的页面路径前缀
const HIDDEN_HEADER_PATHS = [
  '/account',
  '/playDetail',
  '/play',
  '/about/easter-egg',
  '/settings',
  '/about',
  '/help-feedback',
  '/search',
  '/vip',
  '/history',
] as const;

export const Layout: React.FC<LayoutProps> = ({ children, showHeader }) => {
  const location = useLocation();
  const [searchParams] = useSearchParams(); // 获取查询参数（如 ?cid=xxx&vid=yyy）
  const splashNotifiedRef = useRef(false); // 防止重复通知

  // 获取当前路由的上报 pgid
  const pgid = useRouteReport();

  // 自动从 URL 中提取上报参数
  const reportParams = useMemo(() => {
    const params: Record<string, any> = {};
    const { pathname } = location;

    // 1. 从路径中提取参数（手动解析）
    // 频道页: /channel/:channelId/:channelType
    const channelMatch = pathname.match(/^\/channel\/([^/]+)(?:\/([^/]+))?/);
    if (channelMatch) {
      const [, channelId] = channelMatch;
      if (channelId && channelId !== '0') {
        // 过滤掉无效的 channelId
        params.channel_id = channelId;
      }
    }

    // 播放页: /play/:cid/:vid
    const playMatch = pathname.match(/^\/play\/([^/]+)\/([^/]+)/);
    if (playMatch) {
      const [, cid, vid] = playMatch;
      params.cid = cid;
      params.vid = vid;
    }

    // 播放详情页: /playDetail/:cid/:vid
    const playDetailMatch = pathname.match(/^\/playDetail\/([^/]+)(?:\/([^/]+))?/);
    if (playDetailMatch) {
      const [, cid, vid] = playDetailMatch;
      params.cid = cid;
      if (vid) {
        params.vid = vid;
      }
    }

    // 会员开通页: /vip/open/:sourceId
    const vipOpenMatch = pathname.match(/^\/vip\/open\/([^/]+)/);
    if (vipOpenMatch) {
      const [, sourceId] = vipOpenMatch;
      params.source_id = sourceId;
    }

    // 筛选页: /more/:firstcategory
    const moreMatch = pathname.match(/^\/more\/([^/]+)/);
    if (moreMatch) {
      const [, category] = moreMatch;
      params.category = category;
    }

    // 2. 从查询参数中提取（优先级更高，会覆盖路径参数）
    const cidFromQuery = searchParams.get('cid');
    const vidFromQuery = searchParams.get('vid');
    const channelIdFromQuery = searchParams.get('channelId') || searchParams.get('channel_id');

    if (cidFromQuery) {
      params.cid = cidFromQuery;
    }
    if (vidFromQuery) {
      params.vid = vidFromQuery;
    }
    if (channelIdFromQuery) {
      params.channel_id = channelIdFromQuery;
    }

    // 如果没有任何参数，返回 undefined
    return Object.keys(params).length > 0 ? params : undefined;
  }, [location, searchParams]);

  // 启用全局回退导航并获取退出对话框状态
  const { showExitDialog, onExitConfirm, onExitCancel } = useBackNavigation();

  // 判断当前页面是否应该隐藏 Header
  const isHiddenHeaderPage = HIDDEN_HEADER_PATHS.some((path) => location.pathname.startsWith(path));

  // rich版焦点图颜色，用于添加背景渐变层
  const richBannerColor = useChannelStore((state) => state.richBannerColor);
  const richBannerColorText = useMemo(() => {
    if (richBannerColor) {
      return `radial-gradient(
              120% 120% at 0% 0%,
              rgba(${richBannerColor.r}, ${richBannerColor.g}, ${richBannerColor.b}, 0.25) 0%,
              rgba(${richBannerColor.r}, ${richBannerColor.g}, ${richBannerColor.b}, 0.1) 40%,
              rgba(${richBannerColor.r}, ${richBannerColor.g}, ${richBannerColor.b}, 0) 75%
            )`;
    }
    return '';
  }, [richBannerColor]);

  // 如果 showHeader 没有传值，根据页面路径自动判断
  const shouldShowHeader = showHeader ?? !isHiddenHeaderPage;

  useEffect(() => {
    // 初始化响应式适配（只执行一次）
    initResponsive();

    // 初始化空间导航（只执行一次）
    initSpatialNavigation();
  }, []);

  // 路由变化时强制关闭系统键盘
  useEffect(() => {
    forceCloseKeyboard();
  }, [location.pathname]);

  // 播放状态从 playerInstance.currentVideoInfo.state 读取
  const playbackStateVersion = usePlayerStore((state) => state.playbackStateVersion);
  const playbackState = usePlayerStore((state) => state.playerInstance?.currentVideoInfo?.state);
  const pause = usePlayerStore((state) => state.pause);
  void playbackStateVersion;
  const needResetFocus = useChannelStore((state) => state.needResetFocus);

  // 路由变化时，如果切换到非播放页面，自动暂停播放
  useEffect(() => {
    const currentPath = location.pathname;

    // 只有这两个页面需要播放器播放
    const isPlayerPage = currentPath.startsWith('/play/') || currentPath.startsWith('/playDetail/');

    // 如果不是播放相关页面，暂停播放器
    if (!isPlayerPage) {
      // 只有在播放状态时才需要暂停，避免不必要的调用
      if (playbackState === 'playing') {
        logger.log(`[Layout] 路由切换到非播放页面: ${currentPath}，暂停播放`);
        pause().catch((error) => {
          logger.error('[Layout] 路由切换时暂停播放失败:', error);
        });
      }
    }
  }, [location.pathname, playbackState, pause]);

  // 首屏渲染完成后通知启动画面可以移除
  useEffect(() => {
    if (!splashNotifiedRef.current) {
      // 使用 requestIdleCallback 或 setTimeout 确保渲染完成
      const notifySplash = () => {
        if (typeof requestIdleCallback !== 'undefined') {
          requestIdleCallback(
            () => {
              splashNotifiedRef.current = true;
              notifySplashReady();
            },
            { timeout: 1000 } // 最多等待 1 秒
          );
        } else {
          setTimeout(() => {
            splashNotifiedRef.current = true;
            notifySplashReady();
          }, 500);
        }
      };

      // 首次渲染后稍作延迟，确保内容完全绘制
      requestAnimationFrame(() => {
        setTimeout(notifySplash, 300);
      });
    }
  }, []);

  return (
    <ReportProvider pgid={pgid} params={reportParams}>
      <div id={PAGE_CONTAINER_ID} className={styles.container}>
        <div
          className={styles.linearMask}
          style={{
            opacity: richBannerColor === null ? 0 : 1,
            background: richBannerColorText,
          }}
        ></div>
        {shouldShowHeader && <Header skipAutoFocus={needResetFocus} />}
        <main className={styles.main}>{children}</main>

        {/* 退出确认对话框 */}
        <ExitConfirmDialog visible={showExitDialog} onConfirm={onExitConfirm} onCancel={onExitCancel} />
      </div>
    </ReportProvider>
  );
};
